import { useMemo, useState, SyntheticEvent } from "react"
import cn from "classnames"

import { ContactsModal, Icon } from "components"
import { ProfileLink, MySessionsLink } from "app/AppRouter/links"
import { sortDateAndTime } from "modules/Booking/common/utils"
import {
    PaymentSystemType,
    PsychologistAddressDetails,
    ServiceType,
    TimeRequestDateTimes,
} from "utils/interfaces"
import { Column, Columns, Text } from "ui-kit/components-branded/index"
import { SESSION_TYPE } from "modules/Booking/common/constants"
import { getFormatDateWithDayAndTime } from "utils/helpers"
import { OnlineOrOfflineLabel } from "modules/BookingCommon/components"
import { getSingleOrFamilyText } from "modules/BookingCommon/common/utils"
import { CURRENCY } from "utils/constants"
import style from "modules/Booking/AfterPaymentPage/components/SessionInfoCard/style.module.scss"
import { getDate } from "modules/Booking/AfterPaymentPage/components/SessionInfoCard/utils"
import {
    formatTitlesShort,
    getFaceUrl,
    renderCityWithDetails,
} from "utils/helpers/psychologistUtil"
import { formatPrice } from "utils/utils"

interface SessionInfoCardProps {
    title?: string
    sessionType: SESSION_TYPE
    psy: PsychologistAddressDetails
    serviceType: ServiceType
    isOnline: boolean
    psyPhone: string
    psyFullName: string
    price: number
    priceUsd: number
    paymentSystem: PaymentSystemType
    rfc3339_start_datetime?: string
    durationMinutes?: string
    sessionDateTimes?: TimeRequestDateTimes[]
    callEnabled: boolean
    lobbyLink?: string
}

export function SessionInfoCard({
    title = "Информация о сессии",
    sessionType,
    psy,
    isOnline,
    serviceType,
    psyPhone,
    psyFullName,
    priceUsd,
    price,
    paymentSystem,
    rfc3339_start_datetime,
    sessionDateTimes,
}: SessionInfoCardProps) {
    const [isModalShown, setModalIsShown] = useState(false)

    const text = getSingleOrFamilyText(!serviceType.family)

    const faceUrl = getFaceUrl(psy.face_url, psy.hash_id)

    const actualPrice = useMemo(() => {
        if (paymentSystem === PaymentSystemType.STRIPE) {
            return priceUsd
        }
        return price
    }, [paymentSystem])

    const currency = useMemo(
        () => (paymentSystem === PaymentSystemType.STRIPE ? CURRENCY.USD : CURRENCY.RUB),
        [paymentSystem]
    )

    const handleClickLink = (event: SyntheticEvent) => {
        event.stopPropagation()
        setModalIsShown(true)
    }

    return (
        <div className={style.container}>
            <Columns className={cn(style.top, "mb-0")}>
                <Column>
                    <div className={cn(style.divider, "is-flex is-flex-direction-column")}>
                        <Text isAccent className="mgb-4">
                            {title}
                        </Text>
                        <Text className={cn(style.textOnline, "is-flex no-wrap-on-desktop mgb-4")}>
                            <OnlineOrOfflineLabel online={isOnline} />
                            <span className={style.bullet}>•</span>
                            {text}
                        </Text>
                        {sessionType === SESSION_TYPE.DEFAULT && (
                            <>
                                {rfc3339_start_datetime && (
                                    <Text className="mgb-4">{getDate(rfc3339_start_datetime)}</Text>
                                )}
                                <Text>
                                    Оплачено
                                    {actualPrice > 0
                                        ? `: ${formatPrice(actualPrice, currency)}`
                                        : ""}
                                </Text>
                            </>
                        )}
                        {sessionType === SESSION_TYPE.TIME_REQUEST && sessionDateTimes && (
                            <>
                                <Text className="is-block mgb-4">В один из дней:</Text>
                                {sortDateAndTime(sessionDateTimes).map((item, index) => {
                                    return (
                                        <Text className="is-block mgb-4" key={index}>
                                            {item.date &&
                                                item.time &&
                                                getFormatDateWithDayAndTime({
                                                    day: item.date,
                                                    time: item.time,
                                                })}
                                        </Text>
                                    )
                                })}
                            </>
                        )}
                        {sessionType === SESSION_TYPE.DEFAULT && !isOnline && (
                            <a onClick={handleClickLink} className="mgt-4">
                                <Text>Как добраться</Text>
                            </a>
                        )}
                    </div>
                </Column>
                <Column>
                    <div className={style.psydivider}>
                        <ProfileLink
                            profileUrl={psy.profile_url}
                            className={style.header}
                            target="_blank"
                        >
                            <div className={style.avatar}>
                                <img src={faceUrl} alt={psyFullName} srcSet={`${faceUrl} 2x`} />
                            </div>
                            <div className={style.mainInfo}>
                                <div className="mr-1">
                                    <div className={style.name}>{psyFullName}</div>

                                    <div className={style.title}>
                                        {formatTitlesShort(psy.titles)}
                                    </div>
                                </div>
                                <Icon type="arrow-right-gray" size={24} />
                            </div>
                        </ProfileLink>
                    </div>
                </Column>
            </Columns>
            <div className={style.line} />
            <div className={style.bottom}>
                <MySessionsLink target="_blank" className="button-primary">
                    <Text isAccent>К&nbsp;моим сессиям</Text>
                </MySessionsLink>
            </div>
            <ContactsModal
                isOffline={!isOnline}
                isModalShown={isModalShown}
                phone={psyPhone}
                location={
                    !isOnline
                        ? renderCityWithDetails(
                              psy.new_city.name,
                              psy.addresses,
                              psy.city_details,
                              <br />
                          )
                        : psy.video_call_handle
                }
                hideModal={() => setModalIsShown(false)}
            />
        </div>
    )
}
